import { Injectable } from '@angular/core';
import { Qualifier } from 'src/app/types/qualifier';
import { AttributeInfo } from 'src/app/types/@types/sl-v-types';
import { CastingMode, CastingValue } from 'src/app/components/att-casting-view/att-casting-view.component';
import { NumberQualifierService } from './number-qualifier.service';
import { CastingTypeFormat } from 'src/app/components/att-casting-view/formats/casting-type-formats-sel/casting-type-formats-sel.component';
// @ts-ignore
import { LngLat } from '!mapbox-gl';
import { SLMap } from '../../sl-map';

export type GeolocationPropertyOption = '' | 'lattitude' | 'longitude' | 'altitude' | 'accuracy' | 'altitudeAccuracy' | 'heading' | 'timestamp' | 'speed';
export type GeolocationProperty = {
  part: GeolocationPropertyOption,
  value: number | null
}
@Injectable({
  providedIn: 'root'
})
export class GeoLocationQualifierService implements Qualifier {
  static UNKNOWN_ACCURACY = -1;
  static MANUAL_ACCURACY = 0;
  static supportedStats = ['first', 'last'];
  static degreeFormat: 'ddeg' | 'deg-min-sec' = 'deg-min-sec';
  static FromPosition(position: GeolocationPosition, property: GeolocationPropertyOption): GeolocationProperty {
    switch (property) {
      case 'timestamp':
        return { part: 'timestamp', value: position.timestamp }
      default:
        return { part: property, value: position.coords[property] }
    }
  }
  static FromLngLat(coord: LngLat, property: GeolocationPropertyOption) {
    switch (property) {
      case 'longitude':
        return coord.lng;
      case 'lattitude':
        return coord.lat;
      case 'accuracy':
        return GeoLocationQualifierService.MANUAL_ACCURACY;
      case 'timestamp':
        return Math.floor(new Date().getTime());
      default:
        return null;
    }
  }
  static FromString(value: string): number {
    return NumberQualifierService.FromString(value);
  }
  static ToString(value: number, format: CastingTypeFormat, fixed: number): string {
    return NumberQualifierService.ToString(value, format, fixed);
  }
  static ToNumber(value: boolean): number {
    return (value === true) ? 1 : 0;
  }
  public static GetDisplayValue(attribute: AttributeInfo, castingMode: CastingMode, val: any, noHTML = true): string {
    if ([undefined, null, 'null', NaN, 'NaN', ''].indexOf(val) > -1) {
      return '';
    }
    if (!attribute.casting) {
      return val;
    }
    if (!attribute.casting[castingMode]) {
      return val;
    }
    const casting: CastingValue = attribute.casting.display as CastingValue;
    if (!casting) {
      switch (attribute.casting.entry.geoLocPart) {
        case 'lattitude':
        case 'longitude':
          if (attribute.casting && attribute.casting.entry && attribute.casting.entry.fixedDigits) {
            return GeoLocationQualifierService.ToDegreeString(val, attribute.casting.entry.fixedDigits, noHTML);
          } else {
            return GeoLocationQualifierService.ToDegreeString(val, 0, noHTML);
          }
        case 'heading':
          const degSign = noHTML ? '\u00B0' : '&deg;';
          return val + degSign;

        case 'altitude':
        case 'altitudeAccuracy':
          if (attribute.casting.entry.fixedDigits) {
            if ([undefined, null, 0].indexOf(attribute.casting.entry.fixedDigits) > -1) {
              return val.toFixed(0);
            } else {
              return val.ToFixed(attribute.casting.entry.fixedDigits);
            }

          } else if (attribute.casting.display && attribute.casting.display.fixedDigits) {
            if ([undefined, null, 0].indexOf(attribute.casting.display.fixedDigits) > -1) {
              return val.toFixed(0);
            } else {
              return val.ToFixed(attribute.casting.entry.fixedDigits);
            }
          }
          return val + ' meters';
          return Number(val).toFixed(2) + ' meters';
        case 'speed':
          return ' meters/second';
        case 'accuracy':
          return `${val} meters`;
        default:
          return '';
      }
    } else {
      return GeoLocationQualifierService.ToString(val, casting.format, casting.fixedDigits);
    }


  }
  public static GetTextExportValue(attribute: AttributeInfo, castingMode: CastingMode, val: any): string {
    return GeoLocationQualifierService.GetDisplayValue(attribute, castingMode, val, true);
  }
  public static GetNativeValue(attribute: AttributeInfo, castingMode: CastingMode, value: any): any {
    return GeoLocationQualifierService.FromString(value);
  }
  public static SupportsStat(statName): boolean {
    return GeoLocationQualifierService.supportedStats.indexOf(statName) > -1;
  }
  public static SetDegreeFormat(val: 'ddeg' | 'deg-min-sec') {
    GeoLocationQualifierService.degreeFormat = val;
  }
  public static ToDegreeString(val: any, fixedDigits: number = null, noHTML = false) {
    if ([undefined, null].indexOf(val) > -1) {
      return '';
    }
    switch (GeoLocationQualifierService.degreeFormat) {
      case 'ddeg':
        return GeoLocationQualifierService.ToDeg(val, fixedDigits, noHTML);
      case 'deg-min-sec':
        if (fixedDigits === null) {
          fixedDigits = 0;
        }
        return GeoLocationQualifierService.ToDegMinSec(Number(val), fixedDigits, noHTML);
    }
    return '';
  }
  public static dms = /^([^\+\-\d]*)([\+\-]*\d+)[^\d]*(\d+)[^\d]*(\d+\.*\d*)/;
  public static ddeg =/^([+-]*\d+\.\d+)/
  public static GetDDegrees(degrees: string): number {
    if (degrees.match(GeoLocationQualifierService.ddeg)) return Number(degrees);
    if (degrees.match(GeoLocationQualifierService.dms)) return Number(GeoLocationQualifierService.DegFromDMS(degrees));
    return undefined;
  }
  public static DegFromDMS(dms: string) {
    const matches = dms.trim().match(GeoLocationQualifierService.dms);
    if (!matches) return undefined;
    matches.shift();

    const [pre, deg, min, sec] = matches;
    let ddeg = Number(deg);
    switch (pre.trim().toLowerCase()) {
      case 'w':
      case 'west':
        if (ddeg >= 0) ddeg = -ddeg;
        break;
      case 's':
      case 'south':
        if(ddeg >= 0) ddeg = -ddeg;
        break;
    }
    let direction = (ddeg < 1) ? -1 : 1;
    if (!!min) ddeg += ((Number(min) / 60) * direction);
    if (!!sec) ddeg += ((Number(sec) / 3600) * direction);
    return ddeg;
  }
  public static ToDeg(val: any, fixedDigits: number = 4, noHTML: boolean = false, noDegs: boolean = false) {
    let degSign = noHTML ? '\u00B0' : '&deg;';
    if (!!noDegs) degSign = '';
    return Number(val).toFixed(fixedDigits) + degSign;

  }
  public static ToDegMinSec(val: any, fixedDigits: number = 4, noHTML: boolean = false, sep: string = null) {
    const ddeg = Math.abs(val);
    let isNegative = val < 0;
    const deg = Math.floor(ddeg);
    let remainder = ddeg - deg;
    remainder = remainder * 60;
    const mins = Math.floor(remainder);
    remainder = remainder - mins;
    remainder = remainder * 60;
    const secs = Math.round(remainder);
    const sign = isNegative ? '-' : '';
    const degSign = noHTML ? '\u00B0' : '&deg;';
    if (!!sep) {
      return `${sign}${deg}${sep}${mins.toFixed(0).padStart(2, '0')}${sep}${secs.toFixed(0).padStart(2, '0')}`;
    }
    return `${sign}${deg}${degSign}${mins.toFixed(0).padStart(2, '0')}'${secs.toFixed(0).padStart(2, '0')}"`;
    /*let numberVal = Number(val);
    const deg = Math.floor(numberVal);
    numberVal -= deg;
    let minutes = numberVal / 60.0;
    numberVal = minutes - Math.floor(minutes);
    minutes = Math.floor(minutes);
    let seconds = numberVal / 60.0;
    numberVal = seconds - Math.floor(seconds);
    seconds = Math.floor(seconds);
    const minuteStr = ('' + minutes).padStart(2, '0');
    const secondStr = ('' + seconds).padStart(2, '0');
    let secondsRemainder = numberVal.toFixed(fixedDigits).split('.').pop();
    if (numberVal > 0 && fixedDigits > 0) {
      secondsRemainder = '.' + secondsRemainder;
    } else {
      secondsRemainder = '';
    }
    const display = `${deg}&deg;${minuteStr}"${secondStr}'"${secondsRemainder}`;
    return display;
    */

  }
  constructor() { }
  public static GetNumFormat(mode: 'ddeg' | 'deg-min-sec') {
    const degSign = '\u00B0';
    switch (mode) {
      case 'ddeg':
        return `###.0000${degSign}`;
        break;
      case 'deg-min-sec':
        return `[hh]${degSign}mm'ss''`;
        break;
    }

  }
}
